import { z } from 'zod';
import { Claim } from '../..';
import { customBooleanValidator } from './schemas/boolean';
import { customTableSchemaValidator, customTableDefaultValue } from './schemas/custom.table';
import { dashboardConfigSchema } from './schemas/dashboard';
import { goalConfigDefaultValue, goalConfigSchema } from './schemas/goals';
import { stringArrayValidator } from './schemas/string.array';
import { themeConfigSchema, themeDefaultValue } from './schemas/theme.config';
import { userMessagesDefaultValue, userMessagesSchema } from './schemas/user.messages';
import { userPreferencesDefaultValue, userPreferencesSchema } from './schemas/user.preference';
import { xpIntegrationConfigSchema } from './schemas/xp.integration';
import { ClaimType, UsersClaims } from './types';
import {
  officeLoginConfigDefaultValue,
  officeLoginConfigSchema,
} from './schemas/office.login.config';
import { csvStringArrayValidator } from './schemas/csv.string.array';
import { NotifyQuestionedDefaultValue, notifyQuestionedSchema } from './schemas/notify.questioned';
import {
  divisionRoundingDefaultValue,
  divisionRoundingSchema,
} from './schemas/feature.division.rounding';
import {
  documentManagementDefaultValue,
  documentManagementSchema,
} from './schemas/document.management';
import { reportSchemaValidator } from './schemas/reports';
import {
  inactivityLogoutDefaultValue,
  inactivityLogoutSchema,
} from './schemas/feature.inactivity.logout';
import { customUrlValidator } from './schemas/url';
import {
  closureShowHiddenMessageDefaultValue,
  closureShowHiddenMessageSchema,
} from './schemas/closure.show.hidden.message';
export * from './schemas/';
export * from './types';
export * from './translations';
import { userManagementSchema, userManagementDefaultValue } from './schemas/user.management';
import { planGroupManagementSchema } from './schemas/plan.group.management';
export type InferedClaimReturnType<T extends UsersClaims> = {
  data?: ClaimSchemaType<T>;
  success: boolean;
};

export type ClaimSchemaType<T extends UsersClaims> = z.infer<(typeof claimMap)[T]['schema']>;

export const userHasClaim = <T extends UsersClaims>(claim: T, userClaims: Claim[]): boolean =>
  !!userClaims.find(userClaim => userClaim.atributo === claim);

export const findAndParseClaim = <T extends UsersClaims>(
  claim: T,
  userClaims: Claim[],
): InferedClaimReturnType<T> => {
  const { schema } = claimMap[claim];
  const selectedClaim = userClaims.find(userClaim => userClaim.atributo === claim);
  if (!selectedClaim) return { success: false };

  try {
    const parsed = schema.parse(String(selectedClaim.valor));
    return { success: true, data: parsed };
  } catch (e) {
    return { success: false };
  }
};

const handleValidateSchema = (value: string, schemaValidator: z.Schema) => ({
  data: schemaValidator.parse(JSON.parse(value)),
  success: true,
});

export enum AllowedClaimModes {
  Office = 'office',
  Profiles = 'profiles',
  User = 'user',
}

export const claimMap = {
  [UsersClaims.OFFICE_CREDITORS_VIEW]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'View all office creditors',
    description: 'Allow viewing of all creditors in the office',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.CUSTOMIZED_TABLE]: {
    schema: z
      .string()
      .refine(value => handleValidateSchema(value, customTableSchemaValidator))
      .transform(value => {
        const parsedValue: z.infer<typeof customTableSchemaValidator> = JSON.parse(value);
        return parsedValue;
      }),
    default_value: customTableDefaultValue,
    type: ClaimType.OBJECT,
    title: 'Custom table on home page',
    description:
      'Shows a configurable table with the configured columns instead of the default first page table',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.TEAM_CREDITORS_VIEW]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'View team creditors',
    description: 'Enables viewing of creditors in the team',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.CLOSURE_UNFINISHED_VIEW]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'View unfinished closures',
    description: 'Enables viewing of unfinished closures',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.CLOSURE_UPDATE]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Update closures',
    description: 'Enables user to update/view closures rules',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.USER_CREATE]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Create users',
    description: 'Enables creating users',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.PAYROLL_VIEW]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'View payroll',
    description: 'Enables viewing payroll',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.PAYSLIP_VIEW]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'View payslip',
    description: 'Enables viewing payslip',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.PERMISSION_PAYMENTS]: {
    type: ClaimType.BOOLEAN,
    schema: customBooleanValidator,
    title: 'Payments permission',
    description: 'Grants permission for payments',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.BACKOFFICE]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Backoffice permission',
    description: 'Allow user to use backoffice actions',
    user_editable: false,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.MIGRATION_UNION_V2]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Migration Union V2 permission',
    description: 'Allow user to migrate to union v2',
    user_editable: false,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.FEATURE_USER_AMOUNT_AGREEMENT]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Creditor amount agreement',
    description: "Enables feature to agree/disagree/question creditor's received amount",
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.FEATURE_GOALS_CONFIG]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Goals config',
    description: 'Enables viewing closure goals config page',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.FEATURE_SEE_OWNED_COMMISSIONS]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'See owned commissions',
    description: 'Enables viewing owned commissions (even when not receiving any part of it)',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.CUSTOM_REPORTS]: {
    schema: z
      .string()
      .refine(value => handleValidateSchema(value, dashboardConfigSchema))
      .transform(value => {
        const parsedValue: z.infer<typeof dashboardConfigSchema> = JSON.parse(value);
        return parsedValue;
      }),
    type: ClaimType.OBJECT,
    title: 'Custom reports',
    description: 'Configuration for custom reports',
    user_editable: false,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },

  [UsersClaims.ADJUSTMENT_REQUEST_VIEW]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Adjustment request view',
    description:
      'Enables viewing adjustment requests screen (leaders to request, admins to approve)',
    user_editable: false,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },

  [UsersClaims.SHEET_DATA_SOURCE]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Sheet data source',
    description: 'Enables option to import Google sheets directly on closures',
    user_editable: false,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.FEATURE_VARIABLES]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Variables',
    description: 'Enables variables edition pages (inside closure, and on sidebar)',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.UNCAPPED_RULES]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Uncapped rules',
    description: 'Enables use of uncapped rules on closures (percentages bigger than 100%)',
    user_editable: false,
    user_visible: false,
    allowed_modes: [AllowedClaimModes.Office],
  },
  [UsersClaims.FEATURE_TEAM_VARIABLES_VIEW]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Team variables view',
    description:
      "Enables team leaders to view its team's members variables (access via homescreen)",
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.USER_MESSAGES]: {
    schema: z
      .string()
      .refine(value => handleValidateSchema(value, userMessagesSchema))
      .transform(value => {
        const parsedValue: z.infer<typeof userMessagesSchema> = JSON.parse(value);
        return parsedValue;
      }),
    default_value: userMessagesDefaultValue,
    type: ClaimType.OBJECT,
    title: 'User messages',
    description: 'Configuration of custom user messages (displayed on home page)',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },

  [UsersClaims.TEAMS_VIEW_PERMISSION]: {
    schema: z
      .string()
      .refine(value => handleValidateSchema(value, stringArrayValidator))
      .transform(value => {
        const parsedValue: z.infer<typeof stringArrayValidator> = JSON.parse(value);
        return parsedValue;
      }),
    type: ClaimType.STRING_ARRAY,
    title: 'Teams view permission',
    description: 'Grants permission for viewing specified teams',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.CREDITORS_VIEW_PERMISSION]: {
    schema: z
      .string()
      .refine(value => handleValidateSchema(value, stringArrayValidator))
      .transform(value => {
        const parsedValue: z.infer<typeof stringArrayValidator> = JSON.parse(value);
        return parsedValue;
      }),
    type: ClaimType.STRING_ARRAY,
    title: 'Creditors view permission',
    description: 'Grants permission for viewing specified creditors',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.DETAILSTABLE_HIDDEN_COLUMNS]: {
    schema: csvStringArrayValidator,
    type: ClaimType.CSV_STRING_ARRAY,
    title: 'Hidden columns on home page table',
    description: 'Hide specified columns from details table (default home page table)',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.HIDE_DIVISION_DETAILS]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Hide division details',
    description: 'Hide divisions split details from home page table',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.FEATURE_CREDITOR_NAME_ON_RULES]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Creditor name on rules',
    description: 'Replaces all displayed creditor codes with their names',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.OFFICE_LOGO_URL]: {
    schema: customUrlValidator,
    type: ClaimType.STRING,
    title: 'Office logo URL',
    description: 'A public URL with a office logo',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.CREDITOR_REPORT_ROA_VIEW]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Creditor report roa view',
    description: 'Enables individual report with ROA data',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.ALL_COMMISSIONS_TABLE]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'All commissions table',
    description: 'Enables page with all commissions table (comissions and divisions details)',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.GROSS_COMMISSION_AMOUNT]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Gross commission amount sumarize',
    description: 'Enables gross commission amount sumarize on home page table',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.EXPORT_ARBITRARY_FIELDS]: {
    schema: csvStringArrayValidator,
    type: ClaimType.CSV_STRING_ARRAY,
    title: 'Export arbitrary fields',
    description: 'Grants permission to view exported arbitrary fields',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.USER_PREFERENCES]: {
    schema: z
      .string()
      .refine(value => handleValidateSchema(value, userPreferencesSchema))
      .transform(value => {
        const parsedValue: z.infer<typeof userPreferencesSchema> = JSON.parse(value);
        return parsedValue;
      }),
    default_value: userPreferencesDefaultValue,
    type: ClaimType.OBJECT,
    title: 'User preferences',
    description: 'Stores user preferences (frontend)',
    user_editable: false,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.THEME_CONFIG]: {
    schema: z
      .string()
      .refine(value => handleValidateSchema(value, themeConfigSchema))
      .transform(value => {
        const parsedValue: z.infer<typeof themeConfigSchema> = JSON.parse(value);
        return parsedValue;
      }),
    default_value: themeDefaultValue,
    type: ClaimType.OBJECT,
    title: 'Theme config',
    description: 'Stores frontend theme config',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.GENERIC_DATA_IMPORT]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Generic data import',
    description: 'Enables generic data import page',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.DOCUMENT_MANAGEMENT]: {
    schema: z
      .string()
      .refine(value => handleValidateSchema(value, documentManagementSchema))
      .transform(value => {
        const parsedValue: z.infer<typeof documentManagementSchema> = JSON.parse(value);
        return parsedValue;
      }),
    default_value: documentManagementDefaultValue,
    type: ClaimType.OBJECT,
    title: 'Document management',
    description: 'Enables document management page',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.COMPANY_MANAGEMENT]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Company management',
    description: 'Enables company management page',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.APPLY_RULES_FUTURE_CLOSURES]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Apply rules future closures',
    description: 'Enables option to apply rules on future closures (when applying a closure rule)',
    user_editable: false,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office],
  },
  [UsersClaims.NOTIFY_ANSWERED_QUESTION]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Notify answered question',
    description:
      'Enables notifications when a creditor answered a question (should be used along with feature.user.amount.agreement)',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.NOTIFY_QUESTIONED]: {
    schema: z
      .string()
      .refine(value => handleValidateSchema(value, notifyQuestionedSchema))
      .transform(value => {
        const parsedValue: z.infer<typeof notifyQuestionedSchema> = JSON.parse(value);
        return parsedValue;
      }),
    default_value: NotifyQuestionedDefaultValue,
    type: ClaimType.OBJECT,
    title: 'Notify questioned',
    description:
      'Enables notifications when a new question is asked (should be used along with feature.user.amount.agreement)',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.NOTIFY_PAYMENT_RECEIVED]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Notify payment received',
    description: 'Enables notifications when payment is received (notifies paid creditor)',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.DISABLE_SPLITC_REFERRAL]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Disable splitc referral',
    description: 'Disable splitc referral',
    user_editable: false,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.OFFICE_SSO_CONFIG]: {
    schema: z.any(),
    type: ClaimType.OBJECT,
    title: 'Office sso config',
    description: 'Office sso config',
    user_editable: false,
    user_visible: false,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.XP_INTEGRATION_CONFIG]: {
    schema: z
      .string()
      .refine(value => handleValidateSchema(value, xpIntegrationConfigSchema))
      .transform(value => {
        const parsedValue: z.infer<typeof xpIntegrationConfigSchema> = JSON.parse(value);
        return parsedValue;
      }),
    type: ClaimType.OBJECT,
    title: 'XP Api integration config',
    description: 'XP Api integration config',
    user_editable: false,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.DISABLE_ALL_COMMISSIONS_TABLE]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Disable all commissions table',
    description: 'Disables all commissions table, even for team leaders',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.DISABLE_HOME_MONTH_EARNING]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Disable home month earning',
    description: 'Disables the "big eye" month earning on home page',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.GOAL_APPEARANCE_CONFIG]: {
    schema: z
      .string()
      .refine(value => handleValidateSchema(value, goalConfigSchema))
      .transform(value => {
        const parsedValue: z.infer<typeof goalConfigSchema> = JSON.parse(value);
        return parsedValue;
      }),
    default_value: goalConfigDefaultValue,
    type: ClaimType.OBJECT,
    title: 'Goals appearance config',
    description: 'Configuration for goals appearance on home page',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.MANAGE_PERMISSION_PROFILES]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Manage permission profiles',
    description: 'Allows viewing permission profiles management page',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.MANAGE_OFFICE_PERMISSIONS]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Manage office-wide permissions',
    description: 'Allows changing system permissions and behavior for all users',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.OFFICE_LOGIN_CONFIG]: {
    schema: z
      .string()
      .refine(value => handleValidateSchema(value, officeLoginConfigSchema))
      .transform(value => {
        const parsedValue: z.infer<typeof officeLoginConfigSchema> = JSON.parse(value);
        return parsedValue;
      }),
    default_value: officeLoginConfigDefaultValue,
    type: ClaimType.OBJECT,
    title: 'Login policy configuration',
    description: 'Options used to enforce password rotation and other login configuration',
    user_editable: false,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.HIDE_REPORTS]: {
    schema: reportSchemaValidator,
    type: ClaimType.CSV_STRING_ARRAY,
    title: 'Manage reports visibility',
    description: 'Hide selected reports from users',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.HIDE_LOGOUT]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Hide logout',
    description: 'Hides the logout menu option',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.HIDE_SPLITC_LOGO]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Hide SplitC logo',
    description: 'Hides the SplitC logo on the menu',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.FEATURE_DIVISION_ROUNDING]: {
    schema: z
      .string()
      .refine(value => handleValidateSchema(value, divisionRoundingSchema))
      .transform(value => {
        const parsedValue: z.infer<typeof divisionRoundingSchema> = JSON.parse(value);
        return parsedValue;
      }),
    default_value: divisionRoundingDefaultValue,
    type: ClaimType.OBJECT,
    title: 'Division amount rounding configuration',
    description: 'Configures how to round created divisions',
    user_editable: true,
    user_visible: false,
    allowed_modes: [AllowedClaimModes.Office],
  },
  [UsersClaims.FEATURE_INACTIVITY_LOGOUT]: {
    schema: z
      .string()
      .refine(value => handleValidateSchema(value, inactivityLogoutSchema))
      .transform(value => {
        const parsedValue: z.infer<typeof inactivityLogoutSchema> = JSON.parse(value);
        return parsedValue;
      }),
    default_value: inactivityLogoutDefaultValue,
    type: ClaimType.OBJECT,
    title: 'Inactivity logout',
    description: 'Automatically logout based on the user inactivity',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.STATEMENT_PANEL_VISIBLE]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Enable the dashboard',
    description: 'Allows visualization and iteration on the statement panel',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.HIDE_OLD_HOME]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Hide old home page',
    description: 'Hide and prevent user from accessing old home page',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.UPSERT_CUSTOMERS_NAME]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Upsert customers name',
    description: 'Allows to upsert customers name',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.CREDITOR_PAYMENT_INFO_VIEW]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'View creditors payment info',
    description: 'Enables viewing of creditors payment information',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.CREDITOR_PAYMENT_INFO_WRITE]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Manage creditors payment info',
    description: 'Enables managing of creditors payment information',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.INQUIRIES_MANAGEMENT]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Manage inquiries into commission plans',
    description: 'Enable manage inquiries into commission plans',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.ALLOW_USER_IMPERSONATION]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Allows user to impersonate other users',
    description: 'Allows user to impersonate other users that he can see',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.PREVENT_USER_DELETION]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Prevent user deletion',
    description: 'Prevents users from being deleted',
    user_editable: false,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.FEATURE_PAYROLL_V2]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Payroll V2',
    description: 'Enable Payroll V2',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.CLOSURE_SHOW_HIDDEN_MESSAGE]: {
    schema: z
      .string()
      .refine(value => handleValidateSchema(value, closureShowHiddenMessageSchema))
      .transform(value => {
        const parsedValue: z.infer<typeof closureShowHiddenMessageSchema> = JSON.parse(value);
        return parsedValue;
      }),
    type: ClaimType.OBJECT,
    title: 'Closure Show Hidden Message',
    description: 'View closure hidden in home page, but with a message explaining why',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
    default_value: closureShowHiddenMessageDefaultValue,
  },
  [UsersClaims.PAYROLL_STATUS_MANAGER]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Payroll Status Manager',
    description: 'Enables the user to configure payment flow status',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.USER_MANAGEMENT]: {
    schema: z
      .string()
      .refine(value => handleValidateSchema(value, userManagementSchema))
      .transform(value => {
        const parsedValue: z.infer<typeof userManagementSchema> = JSON.parse(value);
        return parsedValue;
      }),
    default_value: userManagementDefaultValue,
    type: ClaimType.OBJECT,
    title: 'User Management',
    description: 'Enables management user',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles, AllowedClaimModes.User],
  },
  [UsersClaims.AUTOMATIC_BACKUP_CLOSURES]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Automatic backup closures',
    description: 'Enables generation backup when update closure',
    user_editable: false,
    user_visible: false,
    allowed_modes: [AllowedClaimModes.Office],
  },
  [UsersClaims.PLAN_GROUP_PERMISSIONS]: {
    schema: z
      .string()
      .refine(value => handleValidateSchema(value, planGroupManagementSchema))
      .transform(value => {
        const parsedValue: z.infer<typeof planGroupManagementSchema> = JSON.parse(value);
        return parsedValue;
      }),
    type: ClaimType.OBJECT,
    title: 'Plan group permissions',
    description: 'Enables management plan groups',
    user_editable: false,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Profiles],
  },
  [UsersClaims.REQUIRED_INQUIRY_TAG]: {
    schema: customBooleanValidator,
    type: ClaimType.BOOLEAN,
    title: 'Required inquiry tag',
    description: 'Make inquiry tag required to create an inquiry',
    user_editable: true,
    user_visible: true,
    allowed_modes: [AllowedClaimModes.Office, AllowedClaimModes.Profiles],
  },
};
